import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Autocomplete,
  Stack,
  Chip,
  InputAdornment,
  IconButton,
  Collapse,
  Tooltip,
  Modal,
  Divider,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import agent from '../api/axios';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PaymentIcon from '@mui/icons-material/Payment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import { getCurrentYear } from '../utils/dateUtils';
import { useTaxYear, useTaxYears } from '../hooks/useApi';
import { useSnackbar } from 'notistack';
import { useRoleAccess } from '../hooks/useRoleAccess';

export default function Payments() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { canIssue1099 } = useRoleAccess();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [year, setYear] = useState<number>(getCurrentYear());
  const { data: years = [] } = useTaxYears();

  // Use the first available year if current year not in list
  useEffect(() => {
    if (years.length > 0 && !years.includes(year)) {
      setYear(years[0]);
    }
  }, [years, year]);

  const {
    data: result,
    isLoading: loading,
    error: queryError,
  } = useTaxYear(year);
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [rowCount, setRowCount] = useState<number>(0);

  // Advanced filtering state
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [selectedContractors, setSelectedContractors] = useState<string[]>([]);
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');

  // 1099 Preview modal state
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<any>(null);

  // Process data when loaded
  const fullData = useMemo(() => {
    if (!result) return [];
    return (result || []).map((r: any) => ({
      id: r.id ?? r._id ?? '',
      employeeId: r.id ?? r._id ?? '',
      payeeName: r.payeeName ?? '',
      address1: r.address1 ?? '',
      address2: r.address2 ?? '',
      city: r.city ?? '',
      state: r.state ?? '',
      postalCode: r.postalCode ?? r.zipCode ?? '',
      amount: Number(r.amount ?? 0),
      taxId: r.taxId ?? r.tin ?? '',
      year: r.year ?? year,
    }));
  }, [result, year]);

  // Get unique contractor names for the filter dropdown
  const contractorOptions = useMemo(() => {
    const names = [...new Set(fullData.map((r: any) => r.payeeName))].filter(
      Boolean
    );
    return names.sort();
  }, [fullData]);

  // Update rows when fullData changes
  useEffect(() => {
    setRowCount(fullData.length);
    setPage(0);
    setRows(fullData.slice(0, pageSize));
  }, [fullData, pageSize]);

  const error = queryError
    ? (queryError as any)?.response?.data?.message ||
      'Failed to load payment data. Please try again.'
    : null;

  useEffect(() => {
    // client-side paging and filtering over fetched data
    let filtered = [...fullData];

    // Filter by selected contractors
    if (selectedContractors.length > 0) {
      filtered = filtered.filter((r) =>
        selectedContractors.includes(r.payeeName)
      );
    }

    // Filter by amount range
    const min = parseFloat(minAmount);
    const max = parseFloat(maxAmount);
    if (!isNaN(min)) {
      filtered = filtered.filter((r) => r.amount >= min);
    }
    if (!isNaN(max)) {
      filtered = filtered.filter((r) => r.amount <= max);
    }

    const start = page * pageSize;
    setRows(filtered.slice(start, start + pageSize));
    setRowCount(filtered.length);
  }, [fullData, page, pageSize, selectedContractors, minAmount, maxAmount]);

  const clearFilters = () => {
    setSelectedContractors([]);
    setMinAmount('');
    setMaxAmount('');
  };

  const hasActiveFilters =
    selectedContractors.length > 0 || minAmount || maxAmount;

  const handlePrint = () => {
    window.print();
  };

  const handlePreview = (row: any) => {
    setPreviewData(row);
    setPreviewOpen(true);
  };

  const handlePreviewDownload = () => {
    if (previewData) {
      handlePdfDownload(previewData);
      setPreviewOpen(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);

  const handlePdfDownload = (row: any) => {
    agent.Tax.download1099PDF(row)
      .then((response: Blob) => {
        const url = URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${row.payeeName ?? row.firstName ?? '1099'}_${
          row.year ?? year
        }_1099.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error('Error downloading PDF', err);
      });
  };

  const handleEmployerPdfDownload = (row: any) => {
    agent.Tax.downloadEmployer1099PDF(row)
      .then((response: Blob) => {
        const url = URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${row.payeeName ?? row.firstName ?? 'Employer1099'}_${
          row.year ?? year
        }_Employer_Copy_1099.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch((err: any) => {
        console.error('Error downloading employer 1099 PDF', err);
        enqueueSnackbar('Failed to download employer 1099 PDF', { variant: 'error' });
      });
  };

  const handleIssue1099 = async (row: any) => {
    try {
      await agent.Form1099.issue({
        employeeId: row.employeeId,
        taxYear: year,
        notes: `1099 issued for ${row.payeeName} - $${row.amount.toFixed(2)}`,
      });
      enqueueSnackbar(`1099 issued for ${row.payeeName}`, {
        variant: 'success',
      });
    } catch (err: any) {
      console.error('Failed to issue 1099:', err);
      const message = err?.response?.data?.message || 'Failed to issue 1099';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleExportCsv = async () => {
    try {
      const blob = await agent.Export.downloadPaymentsCsv(year);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payments_${year}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  };

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [];

    // Employee ID - desktop only
    if (!isMobile) {
      cols.push({
        field: 'employeeId',
        headerName: 'Employee ID',
        width: 220,
      });
    }

    // Name - always shown
    cols.push({
      field: 'payeeName',
      headerName: 'Name',
      width: isMobile ? 150 : 220,
      flex: isMobile ? 1 : 0,
    });

    // Address - desktop only
    if (!isMobile) {
      cols.push({
        field: 'address',
        headerName: 'Address',
        width: 380,
        valueGetter: (params) =>
          `${params.row.address1}${
            params.row.address2 ? `, ${params.row.address2}` : ''
          }, ${params.row.city}, ${params.row.state} ${params.row.postalCode}`,
      });
    }

    // Amount - always shown
    cols.push({
      field: 'amount',
      headerName: 'Amount',
      width: isMobile ? 100 : 140,
      valueGetter: (params) => params.row.amount ?? 0,
      renderCell: (params) =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(Number(params.value)),
      align: 'right',
      headerAlign: 'right',
    });

    // TaxId - always shown
    cols.push({
      field: 'taxId',
      headerName: 'TaxId',
      width: isMobile ? 100 : 140,
      valueGetter: (params) => {
        const t: string = params.row.taxId ?? '';
        return t.length >= 4 ? `XXX-XX-${t.slice(-4)}` : t;
      },
    });

    // PDF column - always shown
    cols.push({
      field: 'pdf',
      headerName: '1099',
      width: isMobile ? 80 : 140,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Preview 1099">
            <IconButton
              size="small"
              onClick={() => handlePreview(params.row)}
              disabled={params.row.pdfVisible === false}
            >
              <VisibilityIcon sx={{ color: 'primary.main' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download 1099">
            <IconButton
              size="small"
              onClick={() => handlePdfDownload(params.row)}
              disabled={params.row.pdfVisible === false}
            >
              <PictureAsPdfIcon sx={{ color: 'error.main' }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    });

    // Employer PDF - desktop only (too wide for mobile)
    if (!isMobile) {
      cols.push({
        field: 'employerPdf',
        headerName: '1099 Employer Form',
        width: 160,
        sortable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={() => handleEmployerPdfDownload(params.row)}
            disabled={params.row.employerPdfVisible === false}
          >
            <PictureAsPdfIcon sx={{ color: 'info.main' }} />
          </IconButton>
        ),
      });

      if (canIssue1099) {
        cols.push({
          field: 'issue1099',
          headerName: 'Issue 1099',
          width: 120,
          sortable: false,
          renderCell: (params) => (
            <Tooltip title="Issue 1099 Form">
              <IconButton
                size="small"
                onClick={() => handleIssue1099(params.row)}
                color="success"
              >
                <SendIcon />
              </IconButton>
            </Tooltip>
          ),
        });
      }
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, canIssue1099]);

  // Mobile Card View for Tax Information
  const renderMobileCards = () => (
    <Grid container spacing={2}>
      {rows.map((row) => (
        <Grid item xs={12} key={row.id}>
          <Card
            sx={{
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent sx={{ p: 2 }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: row.amount >= 600 ? 'success.main' : 'grey.400',
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {row.payeeName?.charAt(0)?.toUpperCase() || <PersonIcon />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={600} noWrap>
                    {row.payeeName || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    ID: {row.employeeId?.slice(0, 8)}...
                  </Typography>
                </Box>
                <Chip
                  label={row.amount >= 600 ? 'Reportable' : 'Under $600'}
                  size="small"
                  color={row.amount >= 600 ? 'success' : 'default'}
                />
              </Box>

              {/* Amount and TIN */}
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="primary">
                      {formatCurrency(row.amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      TIN (Last 4)
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                      XXX-XX-{row.taxId?.slice(-4) || '****'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Address */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Address
                </Typography>
                <Typography variant="body2">
                  {row.address1}
                  {row.address2 && `, ${row.address2}`}
                </Typography>
                <Typography variant="body2">
                  {row.city}, {row.state} {row.postalCode}
                </Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Tax Year: {row.year}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Preview 1099">
                    <IconButton size="small" onClick={() => handlePreview(row)}>
                      <VisibilityIcon sx={{ color: 'primary.main' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download 1099">
                    <IconButton size="small" onClick={() => handlePdfDownload(row)}>
                      <PictureAsPdfIcon sx={{ color: 'error.main' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Employer Copy">
                    <IconButton size="small" onClick={() => handleEmployerPdfDownload(row)}>
                      <PictureAsPdfIcon sx={{ color: 'info.main' }} />
                    </IconButton>
                  </Tooltip>
                  {canIssue1099 && (
                    <Tooltip title="Issue 1099">
                      <IconButton size="small" color="success" onClick={() => handleIssue1099(row)}>
                        <SendIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ mx: 'auto', p: 3 }} className="printable-report">
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
          className="no-print"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PaymentIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Tax Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View payment summaries and issue 1099-MISC forms
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Toggle Filters">
              <Button
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                color={hasActiveFilters ? 'primary' : 'inherit'}
              >
                Filters
                {hasActiveFilters && (
                  <Chip
                    label={
                      selectedContractors.length +
                      (minAmount ? 1 : 0) +
                      (maxAmount ? 1 : 0)
                    }
                    size="small"
                    sx={{ ml: 1, height: 20 }}
                  />
                )}
              </Button>
            </Tooltip>
            <Tooltip title="Print Report">
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                disabled={fullData.length === 0}
              >
                Print
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
              disabled={fullData.length === 0}
            >
              Export CSV
            </Button>
          </Stack>
        </Box>

        {/* Year Selection */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tax Year</InputLabel>
                <Select
                  value={year}
                  label="Tax Year"
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {years.map((y: number) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <Typography variant="body2" color="text.secondary">
                Select tax year to view payment information for 1099 reporting
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Print header - only visible when printing */}
        <Box className="print-only" sx={{ display: 'none', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Tax Information - {year}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generated on {new Date().toLocaleDateString()}
          </Typography>
        </Box>

        {/* Advanced Filters */}
        <Collapse in={showFilters}>
          <Box
            sx={{
              p: 2,
              mb: 3,
              bgcolor: (theme) => theme.palette.custom.softBackground,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
            className="no-print"
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems="flex-start"
            >
              <Autocomplete
                multiple
                options={contractorOptions}
                value={selectedContractors}
                onChange={(_, newValue) =>
                  setSelectedContractors(newValue as string[])
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Contractor"
                    placeholder="Select contractors..."
                    size="small"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option: any, index: number) => (
                    <Chip
                      label={option as string}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
                sx={{ minWidth: 300, flex: 1 }}
              />
              <TextField
                label="Min Amount"
                type="number"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                size="small"
                sx={{ width: 150 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Max Amount"
                type="number"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                size="small"
                sx={{ width: 150 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
              {hasActiveFilters && (
                <Button
                  variant="text"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Clear Filters
                </Button>
              )}
            </Stack>
            {hasActiveFilters && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Showing {rowCount} of {fullData.length} records
              </Typography>
            )}
          </Box>
        </Collapse>

        {/* Error State */}
        {error && !loading && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Content - Cards on mobile, DataGrid on desktop */}
        {!loading && (
          <Box sx={{ mt: 2 }}>
            {isMobile ? (
              <>
                {rows.length === 0 ? (
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No payment records found
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Showing {rows.length} of {rowCount} records
                    </Typography>
                    {renderMobileCards()}
                    {/* Mobile Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={page === 0}
                        onClick={() => setPage(page - 1)}
                      >
                        Previous
                      </Button>
                      <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                        Page {page + 1} of {Math.ceil(rowCount / pageSize)}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={(page + 1) * pageSize >= rowCount}
                        onClick={() => setPage(page + 1)}
                      >
                        Next
                      </Button>
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Box sx={{ height: 600, overflowX: 'auto' }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationMode="server"
                  rowCount={rowCount}
                  page={page}
                  pageSize={pageSize}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  onPageChange={(p) => setPage(p)}
                  onPageSizeChange={(ps) => {
                    setPageSize(ps);
                    setPage(0);
                  }}
                  disableSelectionOnClick
                  aria-label="Payment records and 1099 data grid"
                  sx={{
                    border: 'none',
                    minWidth: 900,
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'background.default',
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* 1099 Preview Modal */}
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            borderRadius: 3,
            width: '90%',
            maxWidth: 700,
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                1099-MISC Preview
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Tax Year {previewData?.year || year}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setPreviewOpen(false)}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
            {previewData && (
              <Box>
                {/* Payer/Recipient Info */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: (theme) => theme.palette.custom.softBackground,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        RECIPIENT (PAYEE)
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                        {previewData.payeeName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {previewData.address1}
                        {previewData.address2 && (
                          <>
                            <br />
                            {previewData.address2}
                          </>
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {previewData.city}, {previewData.state}{' '}
                        {previewData.postalCode}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          TIN (Last 4 digits)
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                        >
                          XXX-XX-{previewData.taxId?.slice(-4) || '****'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: (theme) => theme.palette.custom.primaryTint,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: (theme) =>
                          theme.palette.custom.primaryTintBorder,
                      }}
                    >
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        BOX 7 - NONEMPLOYEE COMPENSATION
                      </Typography>
                      <Typography
                        variant="h3"
                        color="primary"
                        sx={{ fontWeight: 700, mt: 1 }}
                      >
                        {formatCurrency(previewData.amount || 0)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Total payments made during {previewData.year || year}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Form Details */}
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Form Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Form Type
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        1099-MISC
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Tax Year
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {previewData.year || year}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Employee ID
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontFamily: 'monospace',
                          fontSize: 11,
                        }}
                      >
                        {previewData.employeeId?.slice(0, 12) || '—'}...
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={
                          previewData.amount >= 600
                            ? 'Reportable'
                            : 'Under Threshold'
                        }
                        size="small"
                        color={
                          previewData.amount >= 600 ? 'success' : 'default'
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Threshold Warning */}
                {previewData.amount < 600 && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      bgcolor: 'warning.light',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'warning.main',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      ⚠️ This contractor is under the $600 reporting threshold.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      1099-MISC forms are typically only required for payments
                      of $600 or more.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              p: 3,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: (theme) => theme.palette.custom.softBackground,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              onClick={() => setPreviewOpen(false)}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Close
            </Button>
            <Button
              onClick={handlePreviewDownload}
              variant="contained"
              color="error"
              startIcon={<DownloadIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Download 1099 PDF
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
